import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { productDetails } from '../productData';

export const SearchBar = ({ variant = 'desktop' }) => {
  const { lang, p } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const products = p || [];

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const q = query.trim().toLowerCase();
  const results = q
    ? products.filter((p) => {
        const haystack = `${p.name} ${p.model || ''} ${p.tag || ''} ${(p.tags || []).join(' ')}`.toLowerCase();
        return haystack.includes(q);
      }).slice(0, 8)
    : [];

  const goToProduct = (p) => {
    setOpen(false);
    setQuery('');
    if (productDetails[p.slug]) {
      navigate(`/product/${p.slug}`);
    } else {
      navigate('/catalogue');
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={variant === 'mobile'
          ? 'flex items-center gap-2 text-white/80 font-dm text-lg tracking-wider'
          : 'p-2 text-white/70 hover:text-white transition-colors'}
        data-testid={`search-icon-${variant}`}
        aria-label="Search"
      >
        <Search size={variant === 'mobile' ? 22 : 18} />
        {variant === 'mobile' && <span>{lang === 'fr' ? 'Rechercher' : 'Search'}</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={() => setOpen(false)}
          data-testid="search-overlay"
        >
          <div
            className="w-full max-w-2xl bg-[#141414] border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 border-b border-white/10">
              <Search size={20} className="text-[#CE2B37] shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === 'fr' ? 'Rechercher un produit...' : 'Search for a product...'}
                className="flex-1 bg-transparent py-4 text-white font-dm text-base outline-none placeholder:text-white/40"
                data-testid="search-input"
              />
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-white/50 hover:text-white"
                data-testid="search-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {q && results.length === 0 && (
                <p className="px-4 py-6 text-white/50 font-dm text-sm" data-testid="search-no-results">
                  {lang === 'fr' ? 'Aucun produit trouvé.' : 'No products found.'}
                </p>
              )}
              {results.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => goToProduct(p)}
                  className="w-full flex items-center gap-4 px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-white/5"
                  data-testid={`search-result-${p.slug}`}
                >
                  <div className="w-12 h-12 bg-white shrink-0 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bebas text-lg text-white leading-tight truncate">{p.name}</p>
                    <p className="font-dm text-xs text-white/50 uppercase tracking-wider">
                      {p.tag}{p.model ? ` · ${p.model}` : ''}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchBar;
